import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { maintenanceType } from 'config/maintenanceType';
import dayjs from 'dayjs';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { maintenanceActions } from 'reduxStore/maintenance/slice';
import { Loader } from 'zComponents/atoms';
import {
	Button,
	Col,
	Drawer,
	Flex,
	Form,
	Image,
	Input,
	Row,
	Segmented,
	Select,
	Steps,
	Tooltip,
	Typography
} from 'zui/Antd';

// Image imports
import Mechanics from 'assets/images/custom/mechanics.png';
import { Maintenance, MaintenanceBattery, MaintenanceCard, MaintenanceConfiguration } from 'assets/images/svg';
import { selectLockersCategories } from 'reduxStore/categories/selectors';
import { selectDevicesByCategoryID, selectLockersDevices } from 'reduxStore/devices/selectors';
import { selectMaintenanceLogs } from 'reduxStore/maintenance/selectors';
import { selectMaintenanceTypes } from 'reduxStore/types/selectors';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Step } = Steps;

const MaintenanceDeviceDrawer = ({ children, visible: visibleProp, setVisible: setVisibleProp, deviceID }) => {
	// Hooks
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const {
		token: {
			fontWeightStrong,
			colorIcon,
			colorTextDescription,
			fontSizeHeading3,
			marginMD,
			marginXXL,
			marginSM,
			padding,
			marginLG
		}
	} = theme.useToken();

	// Redux state
	const categories = useSelector(selectLockersCategories);
	const devices = useSelector(selectLockersDevices);

	const data = useSelector(selectMaintenanceLogs);
	const maintenanceTypes = useSelector(selectMaintenanceTypes);
	const { loading, addMaintenanceLoading, deleteMaintenanceLog } = useSelector((state) => state.Maintenance);

	// ✅ Local state - Internal visible state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const [selectedCategory, setSelectedCategory] = useState(null);
	const [activeTab, setActiveTab] = useState('Add Maintenance');
	const [maintenanceIssue, setMaintenanceIssue] = useState(null);

	// ✅ Permissions
	const canLogMaintenance = useManagePermittedRoles('logMaintenance');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// Auto-select device and category when deviceID is provided
	useEffect(() => {
		if (deviceID && visible && devices.length > 0) {
			const device = devices.find((d) => d.deviceID === deviceID);
			if (device) {
				setSelectedCategory(device.categoryID);
				form.setFieldsValue({
					categoryID: device.categoryID,
					assetID: deviceID
				});
			}
		}
	}, [deviceID, visible, devices, form]);

	// Filter devices by selected category
	const filteredDevices = useSelector((state) => selectDevicesByCategoryID(state, selectedCategory));

	const handleCategorySelect = (category) => {
		setSelectedCategory(category.value); // Set selected category

		// Reset the locker number field when category changes
		form.setFieldsValue({ assetID: null });
	};

	const handleFinish = (values) => {
		//get data to insert into the maintenance table
		const data = removeEmptyOrNull(values);
		delete data.categoryID;

		// Dispatch action to upsert maintenance record
		dispatch(maintenanceActions.addMaintenanceLog(data));

		//Change Active Tab
		setActiveTab('Maintenance Log');

		// Reset form fields
		form.resetFields();
	};

	const handleCloseDrawer = () => {
		form.resetFields();
		setSelectedCategory(null);
		setActiveTab('Add Maintenance');
		setMaintenanceIssue(null);
		setVisible(false);
	};

	const handleDeleteMaintenanceLogs = (maintenanceID) => {
		dispatch(maintenanceActions.deleteMaintenanceLog(maintenanceID));
	};

	const getIcon = (status) => {
		switch (status) {
			case 'Battery Issues':
				return <MaintenanceBattery />;
			case 'Card Reader Fail':
				return <MaintenanceCard />;
			default:
				return <MaintenanceConfiguration />;
		}
	};

	return (
		<>
			{children ? (
				children
			) : (
				<Tooltip title='Log Maintenance' placement='top'>
					<Button
						type='default'
						style={{
							padding: padding
						}}
						icon={<Maintenance />}
						onClick={() => setVisible(true)}
					/>
				</Tooltip>
			)}

			<Drawer
				placement='right'
				open={visible}
				width={410}
				mask={true}
				styles={{
					header: { border: 0 },
					body: { padding: 0 }
				}}
				closeIcon={false}
				onClose={handleCloseDrawer}
			>
				<Row justify='end'>
					<Button
						onClick={handleCloseDrawer}
						type='text'
						icon={<CloseOutlined style={{ fontSize: fontSizeHeading3, color: colorIcon }} />}
						style={{ position: 'relative', margin: marginMD }}
					/>
				</Row>

				<div style={{ padding: `0px ${marginXXL}px` }}>
					<Row justify='center'>
						<Col span={24}>
							<Segmented
								options={['Add Maintenance', 'Maintenance Log']}
								value={activeTab}
								onChange={setActiveTab}
							/>
						</Col>

						<Col
							span={24}
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: activeTab === 'Add Maintenance' ? 'center' : 'start',
								justifyContent: 'center',
								margin: `${marginXXL}px ${marginMD}px ${marginXXL}px`
							}}
						>
							{activeTab === 'Add Maintenance' ? (
								<>
									<Image
										src={Mechanics}
										style={{
											width: 122,
											height: 122,
											objectFit: 'contain',
											marginBottom: marginSM
										}}
										preview={false}
									/>
									<Title level={4} style={{ textAlign: 'center', margin: 0 }}>
										Log Maintenance
									</Title>
								</>
							) : (
								<Title level={4} style={{ margin: 0 }}>
									Maintenance Log
								</Title>
							)}
						</Col>
					</Row>

					{activeTab === 'Add Maintenance' ? (
						<Form form={form} layout='vertical' onFinish={handleFinish}>
							<Form.Item
								label='Locker Area'
								name='categoryID'
								required={false}
								rules={[{ required: true, message: 'Please select the Locker Area' }]}
								style={{
									width: '100%',
									fontWeight: fontWeightStrong
								}}
							>
								<Select
									style={{
										fontWeight: 400
									}}
									showSearch
									placeholder='Select Locker Category'
									options={categories.map((category) => ({
										value: category.categoryID,
										label: category.name
									}))}
									onChange={(e, value) => handleCategorySelect(value)}
								/>
							</Form.Item>

							<Form.Item
								label='Locker Number'
								name='assetID'
								required={false}
								rules={[{ required: true, message: 'Please select the Locker Number' }]}
								style={{
									width: '100%',
									fontWeight: fontWeightStrong
								}}
							>
								<Select
									placeholder='Select Locker Number'
									options={filteredDevices.map((device) => ({
										value: device.deviceID,
										label: device.name
									}))}
									style={{
										fontWeight: 400
									}}
									showSearch
								/>
							</Form.Item>

							<Form.Item
								label='Maintenance Issue'
								name='maintenanceTypeID'
								required={false}
								rules={[{ required: true, message: 'Please select an issue' }]}
								style={{
									fontWeight: fontWeightStrong
								}}
							>
								<Select
									placeholder='Select'
									style={{
										fontWeight: 400
									}}
									options={maintenanceTypes.map((type) => ({
										value: type.maintenanceTypeID,
										label: type.userFriendlyName
									}))}
									showSearch
									onChange={(e, value) => setMaintenanceIssue(value.label)}
								/>
							</Form.Item>

							{maintenanceIssue === maintenanceType.OTHER && (
								<Form.Item name='note'>
									<TextArea
										style={{ color: colorTextDescription }}
										rows={4}
										placeholder='Additional comments...'
									/>
								</Form.Item>
							)}

							<Row justify='center' style={{ paddingTop: marginLG }}>
								<Form.Item>
									<Button
										type='default'
										style={{ marginRight: marginSM }}
										onClick={handleCloseDrawer}
									>
										Locker Overview
									</Button>
									<Tooltip title={!canLogMaintenance ? permissionDeniedMessage : ''} placement='top'>
										<Button type='primary' htmlType='submit' disabled={!canLogMaintenance}>
											Submit
										</Button>
									</Tooltip>
								</Form.Item>
							</Row>
						</Form>
					) : loading || addMaintenanceLoading || deleteMaintenanceLog ? (
						<Row justify={'center'}>
							<Loader position='relative' />
						</Row>
					) : data.length > 0 ? (
						<Steps
							size='small'
							direction='vertical'
							style={{
								width: '100%' // Ensures Steps take full width
							}}
						>
							{data.map((logs) => {
								const device = devices.find((device) => device.deviceID === logs.assetID);
								const category = categories.find(
									(category) => category.categoryID === device?.categoryID
								);
								const maintenanceType = maintenanceTypes.find(
									(type) => type.maintenanceTypeID === logs.maintenanceTypeID
								);

								return (
									<Step
										key={logs.maintenanceID}
										icon={getIcon(maintenanceType.userFriendlyName)}
										title={
											<Flex justify='space-between' style={{ width: 250 }}>
												<Title level={5} style={{ margin: 0, width: 200 }}>
													{category?.name} - {device?.name}
												</Title>

												<Tooltip
													title={!canLogMaintenance ? permissionDeniedMessage : ''}
													placement='top'
												>
													<Button
														style={{ position: 'absolute', right: 0, top: -5 }}
														type='text'
														icon={<DeleteOutlined />}
														onClick={() => handleDeleteMaintenanceLogs(logs.maintenanceID)}
														disabled={!canLogMaintenance}
													/>
												</Tooltip>
											</Flex>
										}
										description={
											<Text
												style={{
													color: colorTextDescription
												}}
											>
												{dayjs(logs.maintenanceCreatedDate).format('D MMMM') || 'N/A'} -{' '}
												{maintenanceType.userFriendlyName === 'Other'
													? logs.note
													: maintenanceType.userFriendlyName}
											</Text>
										}
										style={{
											marginBottom: 16
										}}
									/>
								);
							})}
						</Steps>
					) : (
						<Text>No maintenance logs found </Text>
					)}
				</div>
			</Drawer>
		</>
	);
};

export default MaintenanceDeviceDrawer;
